/**
 * Entidade ReminderSettings (Configurações do Lembrete).
 * 1:1 com User: um documento por usuário (id = user_id = UID do Firebase Auth).
 * O número para WhatsApp está em `users.phone`; aqui ficam preferências de envio e
 * estado do teste de integração (`message_test_sended`).
 */

import { z } from 'zod';
import { UserRef } from '../user/entity';

/** Par padrão: primeiro lembrete mais cedo (mais dias antes), segundo mais perto da data. */
export const REMINDER_OFFSETS_DAYS_DEFAULT: readonly [number, number] = [7, 1] as const;

/** Horário padrão para disparos no dia (formato 24h HH:mm). */
export const PREFERRED_NOTIFICATION_TIME_DEFAULT = '09:00';

const HH_MM = /^\d{2}:\d{2}$/;

function isValidPreferredTime(s: string): boolean {
  if (!HH_MM.test(s)) return false;
  const [h, m] = s.split(':').map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function normalizeReminderOffsetsDays(val: unknown): [number, number] {
  const def: [number, number] = [...REMINDER_OFFSETS_DAYS_DEFAULT];
  if (!Array.isArray(val) || val.length !== 2) return def;
  const a = Number(val[0]);
  const b = Number(val[1]);
  if (!Number.isInteger(a) || !Number.isInteger(b)) return def;
  if (a < 0 || a > 365 || b < 0 || b > 365) return def;
  if (a === b) return def;
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return [hi, lo];
}

const reminderOffsetsTupleSchema = z
  .tuple([z.number().int().min(0).max(365), z.number().int().min(0).max(365)])
  .refine(([a, b]) => a > b, {
    message: 'O primeiro aviso deve ser mais dias antes da manutenção que o segundo.',
  });

const reminder_offsets_days_field = z.preprocess(
  (val) => normalizeReminderOffsetsDays(val),
  reminderOffsetsTupleSchema
);

const preferred_notification_time_field = z.preprocess((val) => {
  if (typeof val === 'string' && isValidPreferredTime(val)) return val;
  return PREFERRED_NOTIFICATION_TIME_DEFAULT;
}, z.string().regex(/^\d{2}:\d{2}$/));

export const reminderSettingsSchema = z.object({
  id: z.string().min(1), // user_id (UID do Firebase Auth) ou id do doc Firestore conforme persistência
  user_id: z.string().min(1),
  /**
   * Dois momentos em relação à data prevista de manutenção: [mais cedo, mais tarde],
   * em dias antes (ex.: [7, 1] = 7 dias antes e 1 dia antes). Valores distintos, 0–365.
   */
  reminder_offsets_days: reminder_offsets_days_field,
  /** Horário preferido no dia para receber os lembretes, formato "HH:mm" (24h). */
  preferred_notification_time: preferred_notification_time_field,
  /**
   * Teste de envio WhatsApp nas configurações concluído com sucesso.
   * Antigamente persistido em `users.message_test_sended` (migrado na leitura).
   */
  message_test_sended: z.boolean().default(false),
  /**
   * `wamid` da última mensagem de **teste de integração** enviada pela API.
   * Usado no webhook (`statuses`) para confirmar `delivered`/`read` sem confundir com
   * futuros envios de lembrete (outros fluxos devem guardar `wamid` noutro lugar).
   */
  message_test_outbound_wamid: z.string().nullable().default(null),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ReminderSettings = z.infer<typeof reminderSettingsSchema>;

/** Valida e normaliza o par de dias (ordena maior → menor; inválido → default). */
export function parseReminderOffsetsDays(input: unknown): [number, number] {
  return reminderSettingsSchema.pick({ reminder_offsets_days: true }).parse({ reminder_offsets_days: input })
    .reminder_offsets_days;
}

/** Valida HH:mm ou aplica default 09:00. */
export function parsePreferredNotificationTime(input: unknown): string {
  return reminderSettingsSchema.pick({ preferred_notification_time: true }).parse({
    preferred_notification_time: input,
  }).preferred_notification_time;
}

/** Referências Firestore da entidade ReminderSettings. */
export class ReminderSettingsRef {
  static readonly collection = 'reminder_settings';
  static collectionPath(userId: string): string {
    const userPath = UserRef.docPath(userId);
    return `${userPath}/${this.collection}`;
  }
  static docPath(userId: string, id: string): string {
    const collectionPath = this.collectionPath(userId);
    return `${collectionPath}/${id}`;
  }
}
